/**
 * SubtitleStylePreview
 *
 * Live preview of ASS subtitle style settings as a simulated video frame.
 * Shows sample text rendered with the current SubtitleStyle values using
 * CSS text-shadow and outline to approximate what libass will produce.
 *
 * No external dependencies — pure CSS simulation.
 */

import { memo } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import type { SubtitleStyle } from '../types';

interface SubtitleStylePreviewProps {
  style: SubtitleStyle;
}

/** Map ASS position to CSS flex-alignment */
function positionToAlign(position: SubtitleStyle['position']): string {
  switch (position) {
    case 'top':
      return 'items-start';
    case 'middle':
      return 'items-center';
    case 'bottom':
    default:
      return 'items-end';
  }
}

/** Build CSS text-shadow to simulate ASS outline + shadow */
function buildTextShadow(outlineColor: string, outlineWidth: number, shadowDepth: number): string {
  const shadows: string[] = [];
  const w = Math.round(outlineWidth);

  // Simulate outline via multiple thin shadows
  if (w > 0) {
    for (let dx = -w; dx <= w; dx++) {
      for (let dy = -w; dy <= w; dy++) {
        if (dx !== 0 || dy !== 0) {
          shadows.push(`${dx}px ${dy}px 0 ${outlineColor}`);
        }
      }
    }
  }

  // Drop shadow
  if (shadowDepth > 0) {
    const sd = Math.round(shadowDepth);
    shadows.push(`${sd}px ${sd}px ${sd}px rgba(0,0,0,0.8)`);
  }

  return shadows.join(', ');
}

export const SubtitleStylePreview = memo(function SubtitleStylePreview({
  style,
}: SubtitleStylePreviewProps) {
  const { t } = useTranslation();

  const alignClass = positionToAlign(style.position);
  const textShadow = buildTextShadow(style.outline_color, style.outline_width, style.shadow_depth);
  // Scale font down proportionally so it fits inside the 16:9 preview box
  const scaledFontSize = Math.max(10, Math.round(style.font_size * 0.4));
  const scaledMarginV = Math.max(4, Math.round(style.margin_v * 0.15));

  return (
    <div className="panel-glass">
      <h4 className="text-sm font-medium text-white/70 mb-3">
        {t('subtitles.hardBurn.stylePreviewTitle', { defaultValue: 'Style Preview' })}
      </h4>

      {/* 16:9 simulated video frame */}
      <div
        className="relative w-full rounded-lg overflow-hidden bg-black border border-white/10"
        style={{ paddingBottom: '56.25%' /* 16:9 */ }}
      >
        {/* Placeholder background — gradient to simulate a video scene */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, #1a1a2e 0%, #16213e 40%, #0f3460 70%, #1a1a2e 100%)',
          }}
        >
          {/* Fake scene elements */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-1/4 left-1/4 w-1/2 h-1/3 rounded-full bg-blue-500/30 blur-2xl" />
            <div className="absolute bottom-1/3 right-1/4 w-1/3 h-1/4 rounded-full bg-purple-500/20 blur-xl" />
          </div>
        </div>

        {/* Subtitle overlay positioned according to style.position */}
        <div
          className={`absolute inset-0 flex flex-col ${alignClass} items-center pointer-events-none`}
          style={{ paddingBottom: `${scaledMarginV}px`, paddingTop: `${scaledMarginV}px` }}
        >
          <p
            style={{
              fontFamily: style.font_family,
              fontSize: `${scaledFontSize}px`,
              color: style.primary_color,
              textShadow,
              lineHeight: 1.3,
              textAlign: 'center',
              padding: '2px 8px',
              whiteSpace: 'nowrap',
            }}
          >
            {t('subtitles.hardBurn.stylePreviewText', {
              defaultValue: 'Sample subtitle text preview',
            })}
          </p>
        </div>
      </div>

      {/* Style summary chips */}
      <div className="flex flex-wrap gap-2 mt-3">
        <span className="text-xs px-2 py-1 rounded bg-white/5 text-white/50 border border-white/10">
          {style.font_family} {style.font_size}pt
        </span>
        <span className="text-xs px-2 py-1 rounded bg-white/5 text-white/50 border border-white/10 flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-3 rounded-sm border border-white/20 flex-shrink-0"
            style={{ backgroundColor: style.primary_color }}
          />
          {style.primary_color}
        </span>
        <span className="text-xs px-2 py-1 rounded bg-white/5 text-white/50 border border-white/10">
          {t('subtitles.hardBurn.styleOutline', { defaultValue: 'Outline' })} {style.outline_width}
          px
        </span>
        <span className="text-xs px-2 py-1 rounded bg-white/5 text-white/50 border border-white/10 capitalize">
          {style.position}
        </span>
      </div>
    </div>
  );
});
